/* Connecting the book to a broker.
 *
 * The screen knows how to draw a connection, not how to draw any particular
 * broker. Which broker is a choice at the top; the fields under it come from
 * `lib/brokers.ts`, so changing the choice changes the form and nothing here
 * has to be told what changed. That is the whole reason the registry exists —
 * before it, this file named one broker in eleven places.
 *
 * What a connection can be at all is argued in that file: read-only keys, kept
 * on the device, outside the export and outside the sync blob.
 *
 * The one thing that cannot be generic is what pressing the button does, since
 * a statement of share trades and a futures account are not the same answer.
 * Each broker brings its own panel for that, picked by id and by nothing else. */

import { useState } from 'react';
import {
  BROKERS, accountFor, accountsFor, chosenBroker, connectionFor, missingFields,
  type BrokerDef,
} from '../lib/brokers';
import { BrokerLinkUnavailable, canReachBrokers, describe, fetchFlexStatement } from '../lib/brokerLink';
import { parseFlex } from '../state/flex';
import { useStore } from '../state/store';
import { MexcPanel } from './MexcPanel';

export interface PanelProps {
  def: BrokerDef;
  values: Record<string, string>;
  account: string;
  say: (m: string) => void;
  /** True while the form is short of something, or this shell cannot reach out
      at all. The panel still renders — its button does not work. */
  blocked: string | null;
}

/* ---------- Interactive Brokers ---------- */

function IbkrPanel({ values, account, say, blocked }: PanelProps) {
  const { runImport } = useStore();
  const [busy, setBusy] = useState(false);

  const pull = async () => {
    setBusy(true);
    try {
      const xml = await fetchFlexStatement(values['token']!.trim(), values['queryId']!.trim());
      const parsed = parseFlex(xml, account);

      if (parsed.trades.length === 0) {
        return say(parsed.skipped[0]?.why ?? 'The statement had no share trades in it.');
      }

      const done = await runImport({
        account,
        fileName: `IBKR Flex${parsed.accountId ? ` · ${parsed.accountId}` : ''}`,
        trades: parsed.trades,
        // The statement has named fields, so there are no columns to map.
        columns: { at: 0, ticker: 1, side: 2, shares: 3, price: 4 },
      });

      const parts = [`${done.recorded} recorded`];
      if (done.opened) parts.push(`${done.opened} opened`);
      if (done.closed) parts.push(`${done.closed} closed`);
      if (parsed.skipped.length) parts.push(`${parsed.skipped.length} row(s) unreadable`);
      say(`${parts.join(', ')}. Undo it under the broker if it is not right.`);
    } catch (e) {
      say(e instanceof BrokerLinkUnavailable
        ? e.message
        : `Could not read the statement: ${describe(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fab-row" style={{ marginTop: 0 }}>
      <button className="btn btn-primary btn-block" disabled={busy || blocked !== null}
              onClick={() => void pull()}>
        {busy ? 'Reading the statement…' : 'Pull my trades'}
      </button>
    </div>
  );
}

const PANELS: Record<string, (p: PanelProps) => JSX.Element> = {
  ibkr: IbkrPanel,
  mexc: MexcPanel,
};

/* ---------- the form every broker shares ---------- */

export function BrokerConnection({ say }: { say: (m: string) => void }) {
  const { state, saveSettings } = useStore();
  const [reveal, setReveal] = useState(false);

  const def = chosenBroker(state.settings);
  const values = connectionFor(state.settings, def.id);
  const usable = accountsFor(def, state.data.accounts);
  const account = accountFor(state.settings, def, state.data.accounts);
  const reachable = canReachBrokers();
  const missing = missingFields(def, values);
  const hasSecret = def.fields.some((f) => f.secret && (values[f.key] ?? '') !== '');

  /* Written whole so the migrated legacy keys are persisted in the new shape
     the first time anything is edited, rather than being re-read forever. */
  const setField = (key: string, value: string) => void saveSettings({
    brokerFields: { ...state.settings.brokerFields, [def.id]: { ...values, [key]: value } },
  });

  const pickBroker = (id: string) => {
    setReveal(false);
    void saveSettings({ broker: id });
  };

  const forget = () => {
    void saveSettings({
      brokerFields: { ...state.settings.brokerFields, [def.id]: {} },
      // The old flat keys are cleared too, or forgetting a token would leave
      // the migration to hand it straight back on the next render.
      ...(def.id === 'ibkr' ? { ibkrFlexToken: '', ibkrFlexQueryId: '' } : {}),
    });
    setReveal(false);
    say(`${def.name} keys removed from this device.`);
  };

  const blocked = !reachable
    ? 'This copy cannot reach a broker directly.'
    : usable.length === 0
      ? 'There is no account in this book for it to file into.'
      : missing.length > 0
        ? `Still needed: ${missing.map((f) => f.label.toLowerCase()).join(' and ')}.`
        : null;

  const Panel = PANELS[def.id];

  return (
    <>
      <div className="seg" style={{ marginBottom: 14 }}>
        {BROKERS.map((b) => (
          <button key={b.id} aria-pressed={b.id === def.id} onClick={() => pickBroker(b.id)}>
            {b.name}
          </button>
        ))}
      </div>

      <div className="dim" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.5 }}>
        {def.reads}
      </div>

      {!reachable ? (
        <div className="warn" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.45 }}>
          This copy cannot reach a broker directly. A browser tab enforces cross-origin rules that
          neither broker makes any allowance for, so the request cannot be made from here at all.
          The installed apps route it outside the page and can. Everything on this screen still
          saves.
        </div>
      ) : null}

      {def.fields.map((f) => (
        <div className="field" key={`${def.id}-${f.key}`}>
          <label htmlFor={`${def.id}-${f.key}`}>{f.label}</label>
          <input
            id={`${def.id}-${f.key}`}
            className="input"
            data-mono={f.mono ? 'true' : undefined}
            type={f.secret && !reveal ? 'password' : 'text'}
            inputMode={f.numeric ? 'numeric' : undefined}
            autoComplete="off" spellCheck={false}
            value={values[f.key] ?? ''}
            onChange={(e) => setField(f.key, e.target.value)}
          />
          {f.hint ? <div className="hint">{f.hint}</div> : null}
        </div>
      ))}

      <div className="hint" style={{ marginTop: -4, marginBottom: 14 }}>
        Kept on this device only. Not in the JSON export and not in the sync blob, because settings
        sit outside both, and never sent anywhere but to {def.name}. {def.where}
      </div>

      <div className="control-row">
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor={`${def.id}-account`}>
            {def.holds === 'futures' ? 'Read into' : 'Import into'}
          </label>
          <select
            id={`${def.id}-account`} className="select" value={account}
            disabled={usable.length === 0}
            onChange={(e) => void saveSettings({
              brokerAccounts: { ...state.settings.brokerAccounts, [def.id]: e.target.value },
            })}
          >
            {usable.length === 0
              ? <option value="">no account for this</option>
              : usable.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        {hasSecret ? (
          <button className="btn btn-sm" onClick={() => setReveal((v) => !v)}>
            {reveal ? 'Hide keys' : 'Show keys'}
          </button>
        ) : null}
      </div>

      {usable.length === 0 ? (
        <div className="warn" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.45 }}>
          {def.holds === 'futures'
            ? 'No account in this book holds contracts, so there is nowhere for this answer to go. Mark one as a futures account under Accounts first.'
            : 'Every account in this book holds contracts. Add a share account under Accounts first.'}
        </div>
      ) : null}

      {blocked !== null && reachable && usable.length > 0 ? (
        <div className="hint" style={{ marginBottom: 12 }}>{blocked}</div>
      ) : null}

      {Panel ? <Panel def={def} values={values} account={account} say={say} blocked={blocked} /> : null}

      {def.fields.some((f) => (values[f.key] ?? '') !== '') ? (
        <div className="fab-row" style={{ marginTop: 10 }}>
          <button className="btn btn-block" onClick={forget}>Forget these keys</button>
        </div>
      ) : null}
    </>
  );
}
